/*!
 * proximity-graph.js — hover-reactive node field background
 * No dependencies. Canvas 2D. ~7kb unminified.
 *
 * Usage:
 *   import { createProximityGraph } from "../brand/proximityGraph";
 *   const bg = createProximityGraph(document.querySelector('.hero'), { reach: 200 });
 *   bg.destroy();
 */

const DEFAULTS = {
  // Layout
  spacing: 38,              // px between grid points before scatter
  scatter: 0.62,            // 0 = perfect lattice, 1 = heavily scattered
  dropout: 0.14,            // fraction of points removed, for gaps
  sizeVariance: 0.45,       // 0 = every dot identical
  seed: 1,                  // change for a different arrangement at the same settings
  nodes: null,              // optional [{x,y}] with x,y in 0..1 — overrides the generator

  // Interaction
  reach: 185,               // px radius of influence
  ease: 0.085,              // hub follow, 0..1 per frame. lower = heavier
  pointerTarget: null,      // element to listen on. defaults to the host

  // Nodes
  nodeRadius: [0.85, 4.45],
  nodeOpacity: [0.1, 1],
  nodeFalloff: 1.7,         // >1 tightens the bright core

  // Lines
  lines: true,
  lineWidth: 0.7,
  lineOpacity: 0.55,
  lineFalloff: 2,

  // Hub
  hub: true,
  hubRadius: 5.4,

  // Colour
  color: '240, 238, 232',   // rgb triplet, fallback for nodes and lines
  palette: null,            // optional ['r, g, b', …] — one colour picked per node
  lineColor: null,          // rgb triplet for the lines. null = use `color`
  hubColor: '245, 244, 240',
  background: null,         // null = transparent, let CSS own it

  // Idle drift when the pointer is away
  idle: true,
  idleSpeed: 0.006,
  idleAmpX: 0.26,
  idleAmpY: 0.28,

  // Behaviour
  maxDpr: 2,
  pauseWhenOffscreen: true,
  respectReducedMotion: true,
  staticOnTouch: true,      // coarse pointers get one static frame, no rAF
  zIndex: 0,
  className: 'proximity-graph'
};

function rand(i, j, seed) {
  const n = Math.sin(i * 127.1 + j * 311.7 + seed * 74.7) * 43758.5453;
  return n - Math.floor(n);
}

export function createProximityGraph(host, userOptions = {}) {
  if (!host) throw new Error('createProximityGraph: host element is required');
  const o = Object.assign({}, DEFAULTS, userOptions);

  const canvas = document.createElement('canvas');
  canvas.className = o.className;
  canvas.setAttribute('aria-hidden', 'true');
  Object.assign(canvas.style, {
    position: 'absolute',
    inset: '0',
    width: '100%',
    height: '100%',
    display: 'block',
    pointerEvents: 'none',
    zIndex: String(o.zIndex)
  });
  if (o.background) canvas.style.background = o.background;

  if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
  host.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  const dpr = Math.min(window.devicePixelRatio || 1, o.maxDpr);
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  let W = 0, H = 0, nodes = [], raf = null, visible = true, destroyed = false;
  let px = 0, py = 0, tx = 0, ty = 0, hasPointer = false, t = 0;

  const isStatic = () =>
    (o.respectReducedMotion && reducedQuery.matches) || (o.staticOnTouch && coarse);

  function build() {
    nodes = [];
    if (o.nodes && o.nodes.length) {
      for (const n of o.nodes) nodes.push({ x: n.x * W, y: n.y * H, s: n.s || 1, c: n.c || null });
      return;
    }
    const cols = Math.ceil(W / o.spacing) + 2;
    const rows = Math.ceil(H / o.spacing) + 2;
    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < rows; j++) {
        if (rand(i + j * 7, j, o.seed) < o.dropout) continue;
        let x = (i - 0.5) * o.spacing;
        let y = (j - 0.5) * o.spacing;
        // polar offset with sqrt radius spreads points evenly across the disc,
        // which clumps and gaps far more naturally than square jitter
        const ang = rand(i, j, o.seed) * 6.2832;
        const rr = Math.sqrt(rand(j, i, o.seed + 13)) * o.scatter * o.spacing;
        x += Math.cos(ang) * rr;
        y += Math.sin(ang) * rr;
        const s = 1 + (rand(i * 3, j * 5, o.seed + 29) - 0.5) * o.sizeVariance;
        const c = o.palette && o.palette.length
          ? o.palette[Math.floor(rand(i * 5 + 1, j * 3 + 2, o.seed + 41) * o.palette.length)]
          : null;
        nodes.push({ x, y, s, c });
      }
    }
  }

  function resize() {
    const r = host.getBoundingClientRect();
    W = r.width; H = r.height;
    if (!W || !H) return;
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    build();
    if (!hasPointer) { px = tx = W / 2; py = ty = H / 2; }
    if (isStatic()) render();
  }

  function render() {
    ctx.clearRect(0, 0, W, H);
    const R = o.reach, R2 = R * R;
    const [rMin, rMax] = o.nodeRadius;
    const [aMin, aMax] = o.nodeOpacity;

    if (o.lines) {
      ctx.lineWidth = o.lineWidth;
      const lc = o.lineColor || o.color;
      for (const n of nodes) {
        const dx = n.x - px, dy = n.y - py, d2 = dx * dx + dy * dy;
        if (d2 > R2) continue;
        const f = 1 - Math.sqrt(d2) / R;
        ctx.strokeStyle = `rgba(${lc},${Math.pow(f, o.lineFalloff) * o.lineOpacity})`;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(n.x, n.y);
        ctx.stroke();
      }
    }

    for (const n of nodes) {
      const dx = n.x - px, dy = n.y - py, d2 = dx * dx + dy * dy;
      const f = d2 > R2 ? 0 : 1 - Math.sqrt(d2) / R;
      const k = Math.pow(f, o.nodeFalloff);
      ctx.fillStyle = `rgba(${n.c || o.color},${aMin + k * (aMax - aMin)})`;
      ctx.beginPath();
      ctx.arc(n.x, n.y, (rMin + Math.pow(f, 1.5) * (rMax - rMin)) * n.s, 0, 6.2832);
      ctx.fill();
    }

    if (o.hub) {
      ctx.fillStyle = `rgba(${o.hubColor},1)`;
      ctx.beginPath();
      ctx.arc(px, py, o.hubRadius, 0, 6.2832);
      ctx.fill();
    }
  }

  function frame() {
    if (destroyed) return;
    t += o.idleSpeed;
    if (!hasPointer && o.idle) {
      tx = W / 2 + Math.cos(t * 0.9) * W * o.idleAmpX + Math.sin(t * 1.7) * W * 0.06;
      ty = H / 2 + Math.sin(t * 1.25) * H * o.idleAmpY;
    } else if (!hasPointer) {
      tx = W / 2; ty = H / 2;
    }
    px += (tx - px) * o.ease;
    py += (ty - py) * o.ease;
    render();
    raf = requestAnimationFrame(frame);
  }

  function start() {
    if (raf || destroyed || isStatic()) return;
    raf = requestAnimationFrame(frame);
  }
  function stop() {
    if (raf) cancelAnimationFrame(raf);
    raf = null;
  }

  const pointerHost = o.pointerTarget || host;
  const onMove = (e) => {
    const r = host.getBoundingClientRect();
    tx = e.clientX - r.left;
    ty = e.clientY - r.top;
    hasPointer = true;
  };
  const onLeave = () => { hasPointer = false; };

  pointerHost.addEventListener('pointermove', onMove, { passive: true });
  pointerHost.addEventListener('pointerleave', onLeave, { passive: true });

  const ro = new ResizeObserver(resize);
  ro.observe(host);

  let io = null;
  if (o.pauseWhenOffscreen && 'IntersectionObserver' in window) {
    io = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
      visible ? start() : stop();
    }, { threshold: 0 });
    io.observe(host);
  }

  const onMotionChange = () => { stop(); isStatic() ? render() : start(); };
  if (reducedQuery.addEventListener) reducedQuery.addEventListener('change', onMotionChange);

  resize();
  if (!isStatic()) start();

  return {
    canvas,
    setOptions(next = {}) {
      Object.assign(o, next);
      build();
      if (isStatic()) render();
    },
    setNodes(list) {
      o.nodes = list;
      build();
      if (isStatic()) render();
    },
    start,
    stop,
    destroy() {
      destroyed = true;
      stop();
      ro.disconnect();
      if (io) io.disconnect();
      pointerHost.removeEventListener('pointermove', onMove);
      pointerHost.removeEventListener('pointerleave', onLeave);
      if (reducedQuery.removeEventListener) reducedQuery.removeEventListener('change', onMotionChange);
      canvas.remove();
    }
  };
}

